import { useState, useEffect, useMemo } from 'react'
import { Plus, Pencil, Trash2, Building2, Phone, Mail, Upload, Image, MapPin, X, Filter, Users, TrendingUp, Clock, LogIn, CheckCircle } from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

const TRADE_TYPES = [
  'builder', 'roofer', 'plumber', 'electrician', 'tree_surgeon',
  'landscaper', 'kitchen_fitter', 'bathroom_fitter', 'window_fitter',
  'extension_specialist', 'loft_conversion', 'general_contractor', 'other'
]

type ClientStatus = 'live' | 'prospecting' | 'retired'

interface Territory {
  id: number
  name: string
  code: string
  region: string
}

interface ClientTerritory {
  id: number
  territoryId: number
  territoryName: string
  assignedFrom: string
}

interface Client {
  id: number
  companyName: string
  tradeType: string
  contactName: string
  contactEmail: string
  contactPhone: string | null
  address: string | null
  logoPath: string | null
  websiteUrl: string | null
  portfolioImage1: string | null
  portfolioImage2: string | null
  portfolioImage3: string | null
  customPrompt: string | null
  toneGuidelines: string | null
  coldLetterPrompt: string | null
  status: ClientStatus
  isActive: boolean
  createdAt: string
  territories?: ClientTerritory[]
}

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([])
  const [territories, setTerritories] = useState<Territory[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [uploading, setUploading] = useState(false)
  const [selectedTerritories, setSelectedTerritories] = useState<number[]>([])
  const [territorySearch, setTerritorySearch] = useState('')
  // Filters
  const [filterStatus, setFilterStatus] = useState<ClientStatus | 'all'>('all')
  const [filterTerritory, setFilterTerritory] = useState<number | 'all'>('all')
  const [clientTerritoryMap, setClientTerritoryMap] = useState<Record<number, ClientTerritory[]>>({})
  // Client portal login
  const [loginStatus, setLoginStatus] = useState<{ hasLogin: boolean; email?: string } | null>(null)
  const [loginForm, setLoginForm] = useState({ email: '', fullName: '', password: '' })
  const [creatingLogin, setCreatingLogin] = useState(false)
  const [formData, setFormData] = useState({
    company_name: '',
    trade_type: 'builder',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    website_url: '',
    custom_prompt: '',
    tone_guidelines: '',
    cold_letter_prompt: '',
    status: 'prospecting' as ClientStatus,
  })

  useEffect(() => {
    fetchClients()
    fetchTerritories()
  }, [])

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/v1/clients/')
      setClients(response.data)
      // Fetch territories for each client
      const territoryMap: Record<number, ClientTerritory[]> = {}
      for (const client of response.data) {
        try {
          const terrResponse = await api.get(`/api/v1/clients/${client.id}/territories`)
          territoryMap[client.id] = terrResponse.data
        } catch {
          territoryMap[client.id] = []
        }
      }
      setClientTerritoryMap(territoryMap)
    } catch (error) {
      toast.error('Failed to load clients')
    } finally {
      setLoading(false)
    }
  }

  // Filtered clients based on status and territory
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Status filter
      if (filterStatus !== 'all' && client.status !== filterStatus) return false
      // Territory filter
      if (filterTerritory !== 'all') {
        const clientTerrs = clientTerritoryMap[client.id] || []
        if (!clientTerrs.some(t => t.territoryId === filterTerritory)) return false
      }
      return true
    })
  }, [clients, filterStatus, filterTerritory, clientTerritoryMap])

  // Dashboard stats
  const stats = useMemo(() => {
    const live = clients.filter(c => c.status === 'live').length
    const prospecting = clients.filter(c => c.status === 'prospecting').length
    const retired = clients.filter(c => c.status === 'retired').length
    const withTerritories = Object.values(clientTerritoryMap).filter(t => t.length > 0).length
    return { total: clients.length, live, prospecting, retired, withTerritories }
  }, [clients, clientTerritoryMap])

  // Get unique territories that have clients assigned
  const assignedTerritories = useMemo(() => {
    const territoryIds = new Set<number>()
    Object.values(clientTerritoryMap).forEach(terrs => {
      terrs.forEach(t => territoryIds.add(t.territoryId))
    })
    return territories.filter(t => territoryIds.has(t.id))
  }, [clientTerritoryMap, territories])

  const fetchTerritories = async () => {
    try {
      const response = await api.get('/api/v1/territories/?limit=500')
      setTerritories(response.data)
    } catch {
      // Territories fetch failed
    }
  }

  const fetchClientTerritories = async (clientId: number) => {
    try {
      const response = await api.get(`/api/v1/clients/${clientId}/territories`)
      return response.data
    } catch (error) {
      return []
    }
  }

  const fetchLoginStatus = async (clientId: number) => {
    try {
      const response = await api.get(`/api/v1/clients/${clientId}/login-status`)
      setLoginStatus(response.data)
    } catch {
      setLoginStatus(null)
    }
  }

  const handleCreateLogin = async (clientId: number) => {
    if (!loginForm.email || !loginForm.fullName || !loginForm.password) {
      toast.error('All login fields are required')
      return
    }
    if (loginForm.password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    setCreatingLogin(true)
    try {
      await api.post(`/api/v1/clients/${clientId}/create-login`, {
        email: loginForm.email,
        full_name: loginForm.fullName,
        password: loginForm.password,
      })
      toast.success('Client portal login created')
      await fetchLoginStatus(clientId)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create login')
    } finally {
      setCreatingLogin(false)
    }
  }

  const assignTerritory = async (clientId: number, territoryId: number) => {
    try {
      await api.post(`/api/v1/clients/${clientId}/territories`, { territory_id: territoryId })
      toast.success('Territory assigned')
      // Refresh client territories
      const territories = await fetchClientTerritories(clientId)
      setSelectedTerritories(territories.map((t: ClientTerritory) => t.territoryId))
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to assign territory')
    }
  }

  const removeTerritory = async (clientId: number, territoryId: number) => {
    try {
      await api.delete(`/api/v1/clients/${clientId}/territories/${territoryId}`)
      toast.success('Territory removed')
      setSelectedTerritories(prev => prev.filter(id => id !== territoryId))
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to remove territory')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingClient) {
        await api.put(`/api/v1/clients/${editingClient.id}`, formData)
        toast.success('Client updated')
      } else {
        await api.post('/api/v1/clients/', formData)
        toast.success('Client created')
      }
      setShowModal(false)
      setEditingClient(null)
      resetForm()
      fetchClients()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save client')
    }
  }

  const resetForm = () => {
    setFormData({
      company_name: '',
      trade_type: 'builder',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      website_url: '',
      custom_prompt: '',
      tone_guidelines: '',
      cold_letter_prompt: '',
      status: 'prospecting' as ClientStatus,
    })
    setSelectedTerritories([])
    setTerritorySearch('')
  }

  const handleEdit = async (client: Client) => {
    setEditingClient(client)
    setFormData({
      company_name: client.companyName,
      trade_type: client.tradeType,
      contact_name: client.contactName,
      contact_email: client.contactEmail,
      contact_phone: client.contactPhone || '',
      address: client.address || '',
      website_url: client.websiteUrl || '',
      custom_prompt: client.customPrompt || '',
      tone_guidelines: client.toneGuidelines || '',
      cold_letter_prompt: client.coldLetterPrompt || '',
      status: (client.status || 'prospecting') as ClientStatus,
    })
    // Load client's assigned territories
    const clientTerritories = await fetchClientTerritories(client.id)
    setSelectedTerritories(clientTerritories.map((t: ClientTerritory) => t.territoryId))
    setTerritorySearch('')
    // Load login status and prefill
    setLoginStatus(null)
    setLoginForm({
      email: client.contactEmail || '',
      fullName: client.contactName || '',
      password: '',
    })
    fetchLoginStatus(client.id)
    setShowModal(true)
  }

  const getStatusBadge = (status: ClientStatus) => {
    switch (status) {
      case 'live':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
      case 'prospecting':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/30'
      case 'retired':
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30'
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    }
  }

  const handleLogoUpload = async (clientId: number, file: File) => {
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('logo', file)
      await api.post(`/api/v1/clients/${clientId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      toast.success('Logo uploaded')
      fetchClients()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo')
    } finally {
      setUploading(false)
    }
  }

  const handlePortfolioUpload = async (clientId: number, imageNumber: number, file: File) => {
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('image', file)
      await api.post(`/api/v1/clients/${clientId}/portfolio/${imageNumber}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      toast.success(`Portfolio image ${imageNumber} uploaded`)
      fetchClients()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to upload portfolio image')
    } finally {
      setUploading(false)
    }
  }

  const handlePortfolioDelete = async (clientId: number, imageNumber: number) => {
    if (!confirm(`Delete portfolio image ${imageNumber}?`)) return
    try {
      await api.delete(`/api/v1/clients/${clientId}/portfolio/${imageNumber}`)
      toast.success(`Portfolio image ${imageNumber} deleted`)
      fetchClients()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete portfolio image')
    }
  }

  const handleDelete = async (client: Client) => {
    if (!confirm(`Delete client ${client.companyName}?`)) return
    try {
      await api.delete(`/api/v1/clients/${client.id}`)
      toast.success('Client deleted')
      fetchClients()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete client')
    }
  }

  const formatTradeType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Clients</h1>
        <button
          onClick={() => {
            setEditingClient(null)
            setFormData({
              company_name: '',
              trade_type: 'builder',
              contact_name: '',
              contact_email: '',
              contact_phone: '',
              address: '',
              website_url: '',
              custom_prompt: '',
              tone_guidelines: '',
              cold_letter_prompt: '',
              status: 'prospecting' as ClientStatus,
            })
            setShowModal(true)
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Client
        </button>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-slate-700/50 rounded-lg">
            <Users className="w-5 h-5 text-slate-300" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-100">{stats.total}</p>
            <p className="text-xs text-slate-400">Total Clients</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-emerald-400">{stats.live}</p>
            <p className="text-xs text-slate-400">Live</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-400">{stats.prospecting}</p>
            <p className="text-xs text-slate-400">Prospecting</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-slate-600/50 rounded-lg">
            <Building2 className="w-5 h-5 text-slate-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-400">{stats.retired}</p>
            <p className="text-xs text-slate-400">Retired</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2 bg-[#f39c13]/20 rounded-lg">
            <MapPin className="w-5 h-5 text-[#f39c13]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#f39c13]">{stats.withTerritories}</p>
            <p className="text-xs text-slate-400">With Territories</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400">Filters:</span>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as ClientStatus | 'all')}
            className="input py-1.5 px-3 text-sm w-auto"
          >
            <option value="all">All Statuses</option>
            <option value="live">Live</option>
            <option value="prospecting">Prospecting</option>
            <option value="retired">Retired</option>
          </select>
          <select
            value={filterTerritory}
            onChange={(e) => setFilterTerritory(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="input py-1.5 px-3 text-sm w-auto"
          >
            <option value="all">All Territories</option>
            {assignedTerritories.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {(filterStatus !== 'all' || filterTerritory !== 'all') && (
            <button
              onClick={() => { setFilterStatus('all'); setFilterTerritory('all') }}
              className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          <span className="text-sm text-slate-500 ml-auto">
            Showing {filteredClients.length} of {clients.length} clients
          </span>
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div className="card text-center py-12">
          <Building2 className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          {clients.length === 0 ? (
            <>
              <h3 className="text-lg font-medium text-slate-200 mb-2">No clients yet</h3>
              <p className="text-slate-400 mb-4">Get started by adding your first client.</p>
              <button onClick={() => setShowModal(true)} className="btn-primary">
                Add Client
              </button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-slate-200 mb-2">No matching clients</h3>
              <p className="text-slate-400 mb-4">Try adjusting your filters.</p>
              <button 
                onClick={() => { setFilterStatus('all'); setFilterTerritory('all') }} 
                className="btn-secondary"
              >
                Clear Filters
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <div key={client.id} className="card hover:border-slate-600/50 transition-all duration-200">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-start gap-3">
                  {/* Logo */}
                  <div className="relative group">
                    {client.logoPath ? (
                      <img
                        src={`${client.logoPath}`}
                        alt={`${client.companyName} logo`}
                        className="w-12 h-12 object-contain rounded-lg border border-slate-700/50 bg-slate-800/50"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-slate-800/50 rounded-lg border border-slate-700/50 flex items-center justify-center">
                        <Image className="w-6 h-6 text-slate-500" />
                      </div>
                    )}
                    <label className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 cursor-pointer rounded-lg transition-all duration-200">
                      <Upload className="w-4 h-4 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleLogoUpload(client.id, file)
                        }}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-100">{client.companyName}</h3>
                    <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 mt-1">
                      {formatTradeType(client.tradeType)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(client)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-800/50 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(client)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800/50 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="truncate">{client.contactEmail}</span>
                </div>
                {client.contactPhone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <span>{client.contactPhone}</span>
                  </div>
                )}
                {/* Territories */}
                {clientTerritoryMap[client.id]?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-1">
                      {clientTerritoryMap[client.id].map(t => (
                        <span key={t.territoryId} className="text-xs px-1.5 py-0.5 bg-[#f39c13]/10 text-[#f39c13] rounded">
                          {t.territoryName.replace(/ (Borough |District |City |County )?Council$/, '')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-700/30 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full border capitalize ${getStatusBadge(client.status || 'prospecting')}`}>
                    {client.status || 'prospecting'}
                  </span>
                  <label className="text-xs text-[#f39c13] hover:text-[#f5b041] cursor-pointer flex items-center gap-1 transition-colors">
                    <Upload className="w-3 h-3" />
                    {client.logoPath ? 'Change' : 'Logo'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleLogoUpload(client.id, file)
                      }}
                      disabled={uploading}
                    />
                  </label>
                </div>
                <span className="text-xs text-slate-500">{client.contactName}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div>
                <h2 className="text-xl font-semibold text-slate-100 mb-4">
                  {editingClient ? 'Edit Client' : 'Add Client'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Logo Upload + Letter Header Preview */}
                  {editingClient && (
                    <div className="flex gap-4 p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                      {/* Logo upload */}
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-lg border border-slate-700/50 bg-slate-800/50 flex items-center justify-center overflow-hidden">
                          {editingClient.logoPath ? (
                            <img
                              src={`${editingClient.logoPath}`}
                              alt="Logo"
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Image className="w-8 h-8 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm text-slate-300 mb-2">Client Logo</p>
                          <label className="btn-secondary text-sm cursor-pointer inline-flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            {editingClient.logoPath ? 'Change' : 'Upload'}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file && editingClient) handleLogoUpload(editingClient.id, file)
                              }}
                              disabled={uploading}
                            />
                          </label>
                        </div>
                      </div>
                      
                      {/* Letter header preview */}
                      <div className="flex-1 bg-white rounded-lg p-4 shadow-md">
                        <div className="flex justify-between items-start">
                          <div className="text-slate-600 text-[10px]">
                            <p className="font-semibold text-slate-800">Mr John Smith</p>
                            <p>123 Example Street</p>
                            <p>London, SW1A 1AA</p>
                          </div>
                          <div className="text-right">
                            {editingClient.logoPath ? (
                              <img
                                src={`${editingClient.logoPath}`}
                                alt="Logo"
                                className="max-w-[80px] max-h-[40px] object-contain ml-auto"
                              />
                            ) : (
                              <p className="font-bold text-[#f39c13] text-sm">{formData.company_name || 'Your Company'}</p>
                            )}
                          </div>
                        </div>
                        <div className="border-b border-slate-200 mt-2 mb-1"></div>
                        <p className="text-slate-400 text-[9px]">Letter header preview</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <label className="label">Company Name</label>
                      <input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as ClientStatus })}
                        className="input"
                      >
                        <option value="prospecting">Prospecting</option>
                        <option value="live">Live</option>
                        <option value="retired">Retired</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Trade Type</label>
                      <select
                        value={formData.trade_type}
                        onChange={(e) => setFormData({ ...formData, trade_type: e.target.value })}
                        className="input"
                      >
                        {TRADE_TYPES.map(type => (
                          <option key={type} value={type}>{formatTradeType(type)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label">Contact Name</label>
                      <input
                        type="text"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        className="input"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Contact Email</label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Contact Phone</label>
                      <input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        className="input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Address</label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="input"
                        placeholder="Business address"
                      />
                    </div>
                    <div>
                      <label className="label">Website URL</label>
                      <input
                        type="text"
                        value={formData.website_url}
                        onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                        className="input"
                        placeholder="www.example.com"
                      />
                    </div>
                  </div>
                  
                  {/* Portfolio Images - Only show when editing */}
                  {editingClient && (
                    <div className="border-t border-slate-700/50 pt-4 mt-4">
                      <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Portfolio Images (for PDF letters)
                      </h3>
                      <p className="text-xs text-slate-500 mb-3">These images will appear at the bottom of generated letters</p>
                      <div className="grid grid-cols-3 gap-4">
                        {[1, 2, 3].map((num) => {
                          const imageKey = `portfolioImage${num}` as keyof Client
                          const imagePath = editingClient[imageKey] as string | null
                          return (
                            <div key={num} className="relative">
                              <div className="aspect-video rounded-lg border border-slate-700/50 bg-slate-800/50 flex items-center justify-center overflow-hidden">
                                {imagePath ? (
                                  <img
                                    src={`${imagePath}`}
                                    alt={`Portfolio ${num}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="text-center">
                                    <Image className="w-8 h-8 text-slate-500 mx-auto mb-1" />
                                    <span className="text-xs text-slate-500">Image {num}</span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-2 flex gap-2">
                                <label className="flex-1 btn-secondary text-xs cursor-pointer text-center py-1">
                                  {imagePath ? 'Change' : 'Upload'}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0]
                                      if (file && editingClient) handlePortfolioUpload(editingClient.id, num, file)
                                    }}
                                    disabled={uploading}
                                  />
                                </label>
                                {imagePath && (
                                  <button
                                    type="button"
                                    onClick={() => handlePortfolioDelete(editingClient.id, num)}
                                    className="btn-secondary text-xs py-1 px-2 text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Territory Assignment - Only show when editing */}
                  {editingClient && (
                    <div className="border-t border-slate-700/50 pt-4 mt-4">
                      <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Assigned Territories
                      </h3>
                      
                      {/* Selected territories */}
                      {selectedTerritories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {selectedTerritories.map(territoryId => {
                            const territory = territories.find(t => t.id === territoryId)
                            return territory ? (
                              <span 
                                key={territoryId}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-[#f39c13]/20 text-[#f39c13] rounded-full text-xs border border-[#f39c13]/30"
                              >
                                {territory.name}
                                <button
                                  type="button"
                                  onClick={() => removeTerritory(editingClient.id, territoryId)}
                                  className="hover:text-red-400 transition-colors"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ) : null
                          })}
                        </div>
                      )}
                      
                      {/* Territory search and add */}
                      <div className="relative">
                        <input
                          type="text"
                          value={territorySearch}
                          onChange={(e) => setTerritorySearch(e.target.value)}
                          className="input"
                          placeholder={selectedTerritories.length >= 10 ? "Maximum 10 territories reached" : "Search territories to add..."}
                          disabled={selectedTerritories.length >= 10}
                        />
                        {territorySearch && (
                          <div className="absolute z-10 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                            {territories
                              .filter(t => 
                                t.name.toLowerCase().includes(territorySearch.toLowerCase()) &&
                                !selectedTerritories.includes(t.id)
                              )
                              .slice(0, 10)
                              .map(territory => (
                                <button
                                  key={territory.id}
                                  type="button"
                                  onClick={() => {
                                    assignTerritory(editingClient.id, territory.id)
                                    setTerritorySearch('')
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-700 transition-colors flex justify-between items-center"
                                >
                                  <span>{territory.name}</span>
                                  <span className="text-xs text-slate-500">{territory.region}</span>
                                </button>
                              ))
                            }
                            {territories.filter(t => 
                              t.name.toLowerCase().includes(territorySearch.toLowerCase()) &&
                              !selectedTerritories.includes(t.id)
                            ).length === 0 && (
                              <div className="px-3 py-2 text-sm text-slate-500">No territories found</div>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        {selectedTerritories.length}/10 {selectedTerritories.length === 1 ? 'territory' : 'territories'} assigned
                      </p>
                    </div>
                  )}
                  
                  {/* LLM Settings */}
                  <div className="border-t border-slate-700/50 pt-4 mt-4">
                    <h3 className="text-sm font-medium text-slate-300 mb-3">Letter Generation Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="label">Custom LLM Prompt</label>
                        <textarea
                          value={formData.custom_prompt}
                          onChange={(e) => setFormData({ ...formData, custom_prompt: e.target.value })}
                          className="input"
                          rows={3}
                          placeholder="Custom instructions for letter generation..."
                        />
                      </div>
                      <div>
                        <label className="label">Tone Guidelines</label>
                        <textarea
                          value={formData.tone_guidelines}
                          onChange={(e) => setFormData({ ...formData, tone_guidelines: e.target.value })}
                          className="input"
                          rows={2}
                          placeholder="e.g., 'Professional but friendly'"
                        />
                      </div>
                      <div>
                        <label className="label">Cold Letter Instructions</label>
                        <textarea
                          value={formData.cold_letter_prompt}
                          onChange={(e) => setFormData({ ...formData, cold_letter_prompt: e.target.value })}
                          className="input"
                          rows={2}
                          placeholder="LLM instructions for cold-outreach letters (separate from planning letters)"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Client Portal Login - Only show when editing */}
                  {editingClient && (
                    <div className="border-t border-slate-700/50 pt-4 mt-4">
                      <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                        <LogIn className="w-4 h-4" />
                        Client Portal Login
                      </h3>
                      {loginStatus === null ? (
                        <p className="text-sm text-slate-500">Loading login status...</p>
                      ) : loginStatus.hasLogin ? (
                        <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-emerald-300 font-medium">Portal login active</p>
                            <p className="text-xs text-slate-400">{loginStatus.email}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-xs text-slate-500">Create a login so this client can view their approved letters online.</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="label">Login Email</label>
                              <input
                                type="email"
                                value={loginForm.email}
                                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                                className="input"
                                placeholder="client@example.com"
                              />
                            </div>
                            <div>
                              <label className="label">Full Name</label>
                              <input
                                type="text"
                                value={loginForm.fullName}
                                onChange={(e) => setLoginForm({ ...loginForm, fullName: e.target.value })}
                                className="input"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="label">Password</label>
                            <input
                              type="password"
                              value={loginForm.password}
                              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                              className="input"
                              placeholder="Min 8 characters"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCreateLogin(editingClient.id)}
                            disabled={creatingLogin}
                            className="btn-primary text-sm flex items-center gap-2"
                          >
                            <LogIn className="w-4 h-4" />
                            {creatingLogin ? 'Creating...' : 'Create Login'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/50">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingClient ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
